import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import FilterChevron from 'screens/Property/components/FilterChevron';
import { verticalCollapse } from 'utilities/animations';
import { Checkbox, Radio } from 'syngenta-digital-cropwise-react-ui-kit';
import { color } from 'd3';
import useTagsData from 'hooks/useTagsData';
import { TagChip } from 'components/TagsRow';

import './index.css';
import { RADIO_OPTIONS_FLAGS } from '../../constants';

const hasTagName = name => typeof name === 'string' && !!name.trim();

const TasksTagFilter = ({
  tasksData,
  localTasksFiltersConfig,
  handleRadioButtonGroup,
  setSelectedTasksTagIds
}) => {
  const [toggleOptions, setToggleOptions] = useState(false);
  const optionsRef = useRef();
  const { tags: catalogueTags } = useTagsData();

  useEffect(() => {
    verticalCollapse(optionsRef.current, toggleOptions);
  }, [toggleOptions, tasksData, catalogueTags]);

  const taskTags = new Map();
  const collectTags = tasks => {
    tasks.forEach(task => {
      if (!task) return;
      (Array.isArray(task.flags) ? task.flags : []).forEach(flag => {
        const id = typeof flag === 'string' ? flag : flag?.id;
        if (!id) return;
        const previous = taskTags.get(id) || {};
        taskTags.set(id, {
          id,
          name:
            previous.name || (hasTagName(flag?.name) ? flag.name : undefined),
          color: previous.color || flag?.color
        });
      });
      if (Array.isArray(task.subtasks)) collectTags(task.subtasks);
    });
  };
  collectTags(tasksData);

  const catalogueById = new Map(catalogueTags.map(tag => [tag.id, tag]));
  const availableTags = [...taskTags.values()]
    .map(tag => {
      const currentTag = catalogueById.get(tag.id);
      return {
        ...tag,
        name: hasTagName(tag.name) ? tag.name : currentTag?.name,
        color: color(tag.color) ? tag.color : currentTag?.color || tag.color
      };
    })
    .filter(tag => hasTagName(tag.name));

  const selectedIds = localTasksFiltersConfig.showTaskTagsWithIds;

  return (
    <div className="pb-6">
      <button
        type="button"
        className="property-filter-section-header flex justify-between items-center w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-60 focus-visible:ring-inset"
        aria-expanded={toggleOptions}
        onClick={() => setToggleOptions(!toggleOptions)}
      >
        <span className="text-black font-semibold">Tags</span>
        <FilterChevron open={toggleOptions} />
      </button>
      <div
        className="transition-all duration-200 overflow-y-hidden border-neutral-100 px-5 h-full pt-6"
        ref={optionsRef}
        data-testid="collapsible-options"
      >
        <span className="text-neutral-60">Show work orders that contain:</span>
        <Radio.Group
          name="backgroundFieldsOptions"
          className="tasks-tag-filter-radio-group flex flex-col gap-4 mt-3 mb-6"
          value={localTasksFiltersConfig.radioOption}
          onChange={handleRadioButtonGroup}
        >
          <Radio value={RADIO_OPTIONS_FLAGS.anyTags}>
            Any of the selected tags
          </Radio>
          <Radio value={RADIO_OPTIONS_FLAGS.allTags}>All selected tags</Radio>
        </Radio.Group>
        <div className="filter-tags-container">
          {availableTags.map(tag => (
            <Checkbox
              key={tag.id}
              className="property-filter-option crop-zone-tag-option"
              checked={selectedIds.includes(tag.id)}
              onChange={event =>
                setSelectedTasksTagIds(
                  event.target.checked
                    ? [...selectedIds, tag.id]
                    : selectedIds.filter(id => id !== tag.id)
                )
              }
            >
              <TagChip
                tag={{
                  id: tag.id,
                  name: tag.name,
                  color: typeof tag.color === 'string' ? tag.color : ''
                }}
                truncate
              />
            </Checkbox>
          ))}
        </div>
      </div>
    </div>
  );
};

TasksTagFilter.propTypes = {
  tasksData: PropTypes.arrayOf(PropTypes.object).isRequired,
  localTasksFiltersConfig: PropTypes.shape({
    radioOption: PropTypes.string.isRequired,
    showTaskTagsWithIds: PropTypes.arrayOf(PropTypes.string).isRequired
  }).isRequired,
  handleRadioButtonGroup: PropTypes.func.isRequired,
  setSelectedTasksTagIds: PropTypes.func.isRequired
};
export default TasksTagFilter;
